/**
 * TerminationChecklistShow.tsx
 *
 * React-admin `<Show>` page for a single termination checklist item
 * (`GET /terminations/:id`). Displays the full task record plus, for
 * `social_insurance_closing` tasks, the uploaded processing/done proof
 * images (viewable full-size via {@link ProofViewerDialog}). Also renders
 * the same status-changing action (`Confirm` / `Mark Processing` / `Mark Done`)
 * as the list view, via `ChecklistActions`, so a task can be advanced without
 * navigating back to the list.
 *
 * Route: /terminations/:id/show
 */

import React, { useState } from 'react';
import { Show, useRecordContext, usePermissions } from 'react-admin';
import { Box, Button, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { FieldGroup } from '../employees/EmployeeShow';
import {
    PROOF_GATED_TASK_TYPE,
    StatusChip,
    formatTaskType,
    ConfirmButton,
    SocialInsuranceProofControl,
} from './TerminationChecklistList';
import type { TerminationProofSlot } from '../dataProvider';
import ProofViewerDialog from './ProofViewerDialog';

/**
 * A single labeled value, matching the visual style of `EmployeeShow.tsx`'s
 * `<Labeled><TextField/></Labeled>` fields without requiring a react-admin
 * record source (some values here are computed or conditionally absent).
 */
const InfoField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box sx={{ minWidth: 200 }}>
        <Typography variant="caption" color="text.secondary" component="div">
            {label}
        </Typography>
        <Typography variant="body1">{children ?? '—'}</Typography>
    </Box>
);

/**
 * Shows a "View Image" button for one proof slot when it has been uploaded,
 * or a placeholder message when it hasn't. Opens {@link ProofViewerDialog}
 * on click to fetch and display the full-size image.
 */
const ProofField: React.FC<{ taskId: number | string; slot: TerminationProofSlot; label: string; hasProof: boolean }> = ({
    taskId,
    slot,
    label,
    hasProof,
}) => {
    const [dialogOpen, setDialogOpen] = useState(false);

    return (
        <Box sx={{ minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary" component="div">
                {label}
            </Typography>
            {hasProof ? (
                <>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setDialogOpen(true)}
                        data-testid={`view-proof-${slot}-btn-${taskId}`}
                    >
                        View Image
                    </Button>
                    <ProofViewerDialog
                        open={dialogOpen}
                        onClose={() => setDialogOpen(false)}
                        taskId={taskId}
                        slot={slot}
                        label={label}
                    />
                </>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    Not uploaded yet.
                </Typography>
            )}
        </Box>
    );
};

/**
 * Renders the same status-changing control as the list's "Action" column
 * (`ConfirmButton` for the three legacy task types, `SocialInsuranceProofControl`
 * for `social_insurance_closing`), so a task's status can be advanced directly
 * from its detail page. Renders nothing once the task is `done` (nothing left
 * to do — the "Proofs" section already shows the final images) or when the
 * current user lacks the `terminations.manage` permission.
 */
const ChecklistActions: React.FC<{ record: any }> = ({ record }) => {
    const { permissions } = usePermissions<string[]>();
    const canManage = Array.isArray(permissions) && permissions.includes('terminations.manage');

    if (!canManage || record.status === 'done') return null;

    return (
        <FieldGroup title="Actions">
            {record.task_type === PROOF_GATED_TASK_TYPE
                ? <SocialInsuranceProofControl record={record} />
                : <ConfirmButton record={record} />}
        </FieldGroup>
    );
};

/**
 * Renders the checklist item's full field set. Reads the record via
 * `useRecordContext()` — react-admin's `<Show>` already establishes the
 * record context, so no `<SimpleShowLayout>`/field-source wiring is needed
 * for these mostly-conditional, non-editable fields.
 */
const TerminationDetailFields: React.FC = () => {
    const record = useRecordContext();
    if (!record) return null;

    const isProofGated = record.task_type === PROOF_GATED_TASK_TYPE;

    return (
        <Box sx={{ p: 2 }}>
            <FieldGroup title="Employee">
                <InfoField label="Employee">{record.full_name ?? record.employee_id}</InfoField>
                <InfoField label="Employee ID">{record.employee_id}</InfoField>
                {record.employee_email && <InfoField label="Employee Email">{record.employee_email}</InfoField>}
            </FieldGroup>

            <FieldGroup title="Task">
                <InfoField label="Task Type">{formatTaskType(record.task_type ?? '')}</InfoField>
                <InfoField label="Assignee">{record.assignee_email}</InfoField>
                <InfoField label="Due Date">{record.due_date}</InfoField>
                <InfoField label="Status">
                    <StatusChip status={record.status ?? 'pending'} />
                </InfoField>
            </FieldGroup>

            <ChecklistActions record={record} />

            {(record.status === 'processing' || record.status === 'done') && (
                <FieldGroup title="Processing">
                    <InfoField label="Processing By">{record.processing_by}</InfoField>
                    <InfoField label="Processing Date">{record.processing_date}</InfoField>
                </FieldGroup>
            )}

            {record.status === 'done' && (
                <FieldGroup title="Completion">
                    <InfoField label="Completed By">{record.completed_by}</InfoField>
                    <InfoField label="Completed Date">{record.completed_date}</InfoField>
                </FieldGroup>
            )}

            {isProofGated && (
                <FieldGroup title="Proofs">
                    <ProofField
                        taskId={record.id}
                        slot="processing"
                        label="Processing proof"
                        hasProof={!!record.has_processing_proof}
                    />
                    <ProofField
                        taskId={record.id}
                        slot="done"
                        label="Done proof"
                        hasProof={!!record.has_done_proof}
                    />
                </FieldGroup>
            )}
        </Box>
    );
};

/**
 * Detail page for a single termination checklist item.
 *
 * Requires the `terminations.read` permission (enforced via App.tsx resource
 * gate and the backend `GET /terminations/:id` route).
 */
export const TerminationChecklistShow: React.FC = () => (
    <Show>
        <TerminationDetailFields />
    </Show>
);
